import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Volume2, VolumeX, ChevronDown } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";

const HERO_VIDEO = "/hero.mp4";
const HERO_POSTER =
  "https://images.unsplash.com/photo-1584890131712-18ee8e3ed49c?crop=entropy&cs=srgb&fm=jpg&q=85";

const CHAPTERS = [
  {
    num: "01",
    title: "Freeride Soul",
    text: "Né dans la poudreuse des faces nord. Chaque pièce est testée par des riders, en conditions réelles, avant de toucher la rue.",
  },
  {
    num: "02",
    title: "Street Graffiti DNA",
    text: "Le tag est notre signature. Des murs de Montréal aux snowparks des Alpes, l'esthétique S'NO reste brute, urbaine, assumée.",
  },
  {
    num: "03",
    title: "Tech Sans Compromis",
    text: "Membranes 20K/20K, coton 450gsm, coutures thermosoudées. Le niveau technique du haut de gamme, sans le prix du luxe.",
  },
];

const LOOKBOOK = [
  {
    img: "https://images.unsplash.com/photo-1647644768239-6e937d5e9743?crop=entropy&cs=srgb&fm=jpg&q=85",
    tag: "Freeride / Alpes 3200m",
    cls: "col-span-12 md:col-span-7 aspect-[16/10]",
  },
  {
    img: "https://images.unsplash.com/photo-1633292587737-f898a032e562?crop=entropy&cs=srgb&fm=jpg&q=85",
    tag: "Street session 04",
    cls: "col-span-12 md:col-span-5 aspect-[4/5]",
  },
  {
    img: "https://images.unsplash.com/photo-1478700485868-972b69dc3fc4?crop=entropy&cs=srgb&fm=jpg&q=85",
    tag: "Powder day",
    cls: "col-span-12 md:col-span-5 aspect-[4/5]",
  },
  {
    img: "https://images.unsplash.com/photo-1599405653894-8a595f692abf?crop=entropy&cs=srgb&fm=jpg&q=85",
    tag: "Summit line",
    cls: "col-span-12 md:col-span-7 aspect-[16/10]",
  },
];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  };

  return (
    <div data-testid="home-page">
      <Seo
        title="S'NO — Streetwear Snowboard Technique"
        siteName="S'NO"
        description="S'NO : hoodies, vestes 20K, bonnets et tees techniques. Streetwear né entre Montréal et les Alpes. Ride the undercurrent."
        image="/logo.webp"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "S'NO",
          description: "Streetwear snowboard technique — Montréal × Les Alpes",
        }}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative h-screen min-h-[640px] overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: videoY, scale: videoScale }} className="absolute inset-0">
          <img
            src={HERO_POSTER}
            alt="Rider S'NO en freeride"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            src={HERO_VIDEO}
            poster={HERO_POSTER}
            autoPlay
            muted
            loop
            playsInline
            data-testid="hero-video"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-black/40 to-black/30" />

        <motion.div
          style={{ opacity: contentOpacity }}
          className="relative z-10 h-full flex flex-col justify-end px-4 sm:px-6 lg:px-12 pb-24 sm:pb-28"
        >
          <MaskedLine delay={0.15}>
            <span className="font-mono text-xs sm:text-sm uppercase tracking-[0.4em] text-ice">
              Montréal — Les Alpes / FW26 Drop
            </span>
          </MaskedLine>
          <h1 className="font-display font-black uppercase leading-[0.85] tracking-tight mt-4">
            <MaskedLine delay={0.3}>
              <span className="text-6xl sm:text-8xl lg:text-[10rem] text-white">Ride the</span>
            </MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="text-6xl sm:text-8xl lg:text-[10rem] text-stroke-ice">Undercurrent</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.65} className="max-w-xl">
            <p className="text-sm sm:text-base text-zinc-300 leading-relaxed mt-6">
              Vêtements techniques de snowboard à l'ADN street. Vestes 20K, hoodies 450gsm,
              tags graff et zéro compromis.
            </p>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap gap-4 mt-8"
          >
            <Link to="/boutique" className="btn-ice" data-testid="hero-cta-collection">
              Découvrir la collection <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/galerie" className="btn-ghost" data-testid="hero-cta-lookbook">
              Le lookbook
            </Link>
          </motion.div>
        </motion.div>

        <button
          data-testid="hero-mute-toggle"
          onClick={toggleMute}
          aria-label={muted ? "Activer le son" : "Couper le son"}
          className="absolute bottom-6 right-4 sm:right-6 lg:right-12 z-20 p-3 border border-white/25 text-white hover:border-ice hover:text-ice transition-colors duration-300 bg-black/40 backdrop-blur"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-zinc-500 animate-bounce">
          <ChevronDown className="w-5 h-5" />
        </div>
      </section>

      <Marquee />

      {/* MANIFESTO */}
      <section className="px-4 sm:px-6 lg:px-12 py-20 sm:py-28 border-t border-white/10" data-testid="manifesto-section">
        <p className="font-mono text-xs uppercase tracking-[0.4em] text-ice mb-3">Manifeste</p>
        <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-none mb-16">
          Ce qui nous <span className="text-stroke">fait rider</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-10">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="border-t border-white/15 pt-8 group hover:border-ice transition-colors duration-500"
              data-testid={`manifesto-chapter-${c.num}`}
            >
              <p className="font-display font-black text-7xl text-stroke opacity-60 group-hover:opacity-100 group-hover:[-webkit-text-stroke-color:rgba(0,240,255,0.8)] transition-all duration-500">
                {c.num}
              </p>
              <h3 className="font-display uppercase tracking-wide text-2xl mt-6 mb-4">{c.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* LOOKBOOK TEASER */}
      <section className="px-4 sm:px-6 lg:px-12 py-20 sm:py-28 border-t border-white/10" data-testid="lookbook-teaser">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.4em] text-ice mb-3">Moodboard</p>
            <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">
              Rue &amp; sommets
            </h2>
          </div>
          <Link
            to="/galerie"
            data-testid="lookbook-see-all"
            className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-ice transition-colors"
          >
            Explorer la galerie <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-12 gap-4">
          {LOOKBOOK.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className={`${item.cls} relative overflow-hidden border border-white/10 group`}
            >
              <img
                src={item.img}
                alt={item.tag}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-ice opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                {item.tag}
              </span>
            </motion.div>
          ))}
        </div>
      </section>

    </div>
  );
}
